import { Outlet, getRouteApi } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import { useIntl } from "react-intl";
import { useOpenQuickFind } from "./QuickFindHost.tsx";
import { Sidebar } from "./Sidebar.tsx";
import { keysLabel } from "./quickfind.ts";
import { useWorkspace } from "./useWorkspace.ts";

const route = getRouteApi("/w/$workspaceId");
const HIDDEN = "paginae:sidebar-hidden";

function loadHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN) === "1";
  } catch {
    return false;
  }
}

/** Sidebar plus content for everything inside a workspace. Quick Find is the app's (QuickFindHost). */
export function WorkspaceLayout() {
  const intl = useIntl();
  const { workspaceId } = route.useParams();
  const { workspace } = useWorkspace(workspaceId);
  const openQuickFind = useOpenQuickFind();
  // The sidebar can be hidden (ADR-0087): with its « button, Ctrl/⌘+\ or the palette; this
  // browser remembers it.
  const [hidden, setHidden] = useState(loadHidden);
  const toggle = () =>
    setHidden((was) => {
      try {
        localStorage.setItem(HIDDEN, was ? "0" : "1");
      } catch {
        // Remembering is a convenience only.
      }
      return !was;
    });
  const latest = useRef({ hidden, intl });
  useEffect(() => {
    latest.current = { hidden, intl };
  });
  useEffect(() => {
    let stop: (() => void) | null = null;
    let live = true;
    void import("./sidebar-toggle.ts").then((m) => {
      if (!live) return;
      stop = m.installSidebarToggle(
        toggle,
        () =>
          latest.current.intl.formatMessage({
            id: latest.current.hidden ? "command.showSidebar" : "command.hideSidebar",
          }),
        () => latest.current.intl.formatMessage({ id: "sidebar.resize" }),
      );
    });
    return () => {
      live = false;
      stop?.();
    };
  }, []);
  const label = intl.formatMessage(
    { id: hidden ? "sidebar.show" : "sidebar.hide" },
    { keys: keysLabel(["Mod", "\\"]) },
  );

  return (
    <div className={hidden ? "workspace-shell sidebar-hidden" : "workspace-shell"}>
      {workspace && !hidden && (
        <Sidebar
          workspace={workspace}
          onSearch={openQuickFind}
          toggle={
            <Button
              className="button button-quiet button-small sidebar-toggle"
              aria-label={label}
              onPress={toggle}
            >
              <span aria-hidden="true">«</span>
            </Button>
          }
        />
      )}
      <main id="main" tabIndex={-1} className="workspace-main">
        {hidden && (
          <Button
            className="button button-quiet button-small sidebar-toggle"
            aria-label={label}
            onPress={toggle}
          >
            <span aria-hidden="true">»</span>
          </Button>
        )}
        <Outlet />
      </main>
    </div>
  );
}
